import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * Mosquito2D: tiny flying mosquito silhouettes on a plain Canvas 2D layer.
 * Deliberately subtle, so the hero stays premium instead of noisy.
 */
export default function Mosquito2D({ count }) {
  const canvasRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    if (!soundOn || typeof window === 'undefined') return undefined;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = new AudioCtx();
    const master = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    const buzz = ctx.createOscillator();
    const shimmer = ctx.createOscillator();

    master.gain.value = 0.0001;
    filter.type = 'bandpass';
    filter.frequency.value = 960;
    filter.Q.value = 2.1;
    buzz.type = 'sawtooth';
    buzz.frequency.value = 540;
    shimmer.type = 'triangle';
    shimmer.frequency.value = 730;

    buzz.connect(filter);
    shimmer.connect(filter);
    filter.connect(master);
    master.connect(ctx.destination);

    const now = ctx.currentTime;
    master.gain.setTargetAtTime(0.018, now, 0.08);
    buzz.start();
    shimmer.start();

    const wobble = window.setInterval(() => {
      const t = ctx.currentTime;
      buzz.frequency.setTargetAtTime(470 + Math.random() * 170, t, 0.05);
      shimmer.frequency.setTargetAtTime(660 + Math.random() * 190, t, 0.05);
      filter.frequency.setTargetAtTime(820 + Math.random() * 360, t, 0.08);
      master.gain.setTargetAtTime(0.012 + Math.random() * 0.013, t, 0.08);
    }, 120);

    return () => {
      window.clearInterval(wobble);
      const t = ctx.currentTime;
      master.gain.setTargetAtTime(0.0001, t, 0.04);
      window.setTimeout(() => {
        try { buzz.stop(); } catch (_) {}
        try { shimmer.stop(); } catch (_) {}
        ctx.close().catch(() => {});
      }, 120);
    };
  }, [soundOn]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const parent = canvas.parentElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const fit = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };

    let { w, h } = fit();
    const isMobile = window.innerWidth < 768;
    const total = typeof count === 'number' ? count : (isMobile ? 8 : 16);
    const mosquitoes = Array.from({ length: total }, () => ({
      x: Math.random() * w,
      y: h * 0.14 + Math.random() * h * 0.62,
      size: 1.35 + Math.random() * 1.1,
      vx: (Math.random() > 0.5 ? 1 : -1) * (0.015 + Math.random() * 0.035),
      vy: (Math.random() - 0.5) * 0.018,
      angle: Math.random() * Math.PI * 2,
      turn: (Math.random() - 0.5) * 0.0008,
      wing: Math.random() * Math.PI * 2,
      wingSpeed: 0.04 + Math.random() * 0.035,
      opacity: 0.42 + Math.random() * 0.36,
    }));

    const onResize = () => {
      const next = fit();
      w = next.w;
      h = next.h;
    };
    window.addEventListener('resize', onResize);

    let raf;
    let last = performance.now();
    let visible = true;
    const onVisibility = () => {
      visible = !document.hidden;
      if (visible) last = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const drawMosquito = (m) => {
      const s = m.size;
      const flap = Math.sin(m.wing) * 1.4;
      ctx.save();
      ctx.translate(m.x, m.y);
      ctx.rotate(m.angle);
      ctx.globalAlpha = m.opacity;

      ctx.strokeStyle = 'rgba(31, 29, 26, 0.78)';
      ctx.fillStyle = 'rgba(31, 29, 26, 0.72)';
      ctx.lineWidth = 1.15 * s;
      ctx.lineCap = 'round';

      // Body and head
      ctx.beginPath();
      ctx.ellipse(0, 0, 4.3 * s, 1.25 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(4.6 * s, 0, 1.25 * s, 0, Math.PI * 2);
      ctx.fill();

      // Wings
      ctx.fillStyle = 'rgba(255, 255, 248, 0.32)';
      ctx.strokeStyle = 'rgba(31, 29, 26, 0.18)';
      ctx.lineWidth = 0.7 * s;
      ctx.beginPath();
      ctx.ellipse(-0.8 * s, (-3.1 + flap) * s, 4.4 * s, 1.55 * s, -0.35, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(-0.9 * s, (3.1 - flap) * s, 4.4 * s, 1.55 * s, 0.35, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Legs
      ctx.strokeStyle = 'rgba(31, 29, 26, 0.62)';
      ctx.lineWidth = 0.75 * s;
      for (let i = -1; i <= 1; i += 1) {
        const x = (-1.2 + i * 1.8) * s;
        ctx.beginPath();
        ctx.moveTo(x, 0.9 * s);
        ctx.lineTo((x - 2.8) * s, 4.8 * s);
        ctx.moveTo(x, -0.9 * s);
        ctx.lineTo((x - 2.8) * s, -4.8 * s);
        ctx.stroke();
      }

      // Proboscis
      ctx.strokeStyle = 'rgba(31, 29, 26, 0.7)';
      ctx.lineWidth = 0.55 * s;
      ctx.beginPath();
      ctx.moveTo(5.6 * s, 0);
      ctx.lineTo(10.2 * s, -0.7 * s);
      ctx.stroke();

      ctx.restore();
    };

    const tick = (time) => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      const dt = Math.min(50, time - last);
      last = time;

      ctx.clearRect(0, 0, w, h);
      for (const m of mosquitoes) {
        m.angle += m.turn * dt + Math.sin(time * 0.00045 + m.x) * 0.0009 * dt;
        m.wing += m.wingSpeed * dt;
        m.x += Math.cos(m.angle) * m.vx * dt + Math.sin(time * 0.001 + m.y) * 0.025 * dt;
        m.y += Math.sin(m.angle) * m.vy * dt + Math.cos(time * 0.0008 + m.x) * 0.018 * dt;

        if (m.x < -24) m.x = w + 24;
        if (m.x > w + 24) m.x = -24;
        if (m.y < h * 0.08) m.y = h * 0.08;
        if (m.y > h * 0.82) m.y = h * 0.82;

        drawMosquito(m);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [count]);

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="mosquito-2d-canvas"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          display: 'block',
        }}
      />
      <button
        type="button"
        onClick={() => setSoundOn((active) => !active)}
        aria-pressed={soundOn}
        aria-label={soundOn ? 'Mueckenton ausschalten' : 'Mueckenton einschalten'}
        title={soundOn ? 'Mueckenton ausschalten' : 'Mueckenton einschalten'}
        className="absolute right-4 bottom-4 sm:right-6 sm:bottom-6 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[#1F1D1A] shadow-[0_14px_32px_-18px_rgba(31,29,26,0.45)] backdrop-blur-md transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F26B1A]/50"
      >
        {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
      </button>
    </>
  );
}
